import _ from "lodash/fp";
import { BehaviorSubject } from "rxjs";
import { maskTypename } from "@urql/core";

import {
  adjustMathMLSpacing,
  adjustTableFormattingForAPI,
  adjustTableFormattingForUI,
  convertUTF8ToHTMLEntities,
  encodeMathMLTags,
  getCharacterCount,
  getHasUnresolvedChangeTags,
  getTextToSpeechTags,
  orderDynamicTags,
  removeCdeIds,
  removeClassMsoNormalAttr,
  removeFontFamilyFromStyle,
  removeUnnecessaryMarkTags,
  removeLabelfromTTS,
  unwrapElements,
  removeFontWeight400FromStyle,
} from "../../services/contentUtils";

import { deriveKeyValueFromInteraction } from "../../services/item";
import {
  canSave,
  getAssetInfo,
  getTasksByObjects,
  uniqueObjects,
  updateTasks,
} from "../../services/utils";

import {
  dataSourceOptions,
  relationshipTypeOptions,
} from "../../routes/Workspace/MetadataCard/constants";
import { applyOptimisticRetryStatuses } from "../../providers/TTSFileStatusProvider";

const subject = new BehaviorSubject(null);
let client;

const CHARS_PER_STANDARD_WORD = 6;

const calculateAverageStandardWordCount = (
  items = [],
  shouldIncrementCount = false,
) => {
  if (!Array.isArray(items) || !items.length) {
    return null;
  }

  const counts = items
    .map((item) => Number(item?.pbtCharacterCount))
    .filter((count) => Number.isFinite(count));

  if (!counts.length) {
    return null;
  }
  const totalCharacters = counts.reduce((sum, count) => sum + count, 0);
  const denominator = shouldIncrementCount ? counts.length + 1 : counts.length;
  const averageWords = totalCharacters / denominator / CHARS_PER_STANDARD_WORD;

  if (!Number.isFinite(averageWords)) {
    return null;
  }

  if (averageWords === 0) {
    return 0;
  }

  return Math.round(averageWords);
};

const store = {
  client,
  /**
   * Fetch data from the graphQL API and write the response to the stream
   *
   * @param {string} id
   */
  fetch: (users) => (programs) => async (id) => {
    // If this was called without a value for the ID, send a null to the stream and exit
    if (!id) {
      return subject.next(null);
    }

    try {
      // Query the graphQL API
      const promises = [
        client.query(getPassageQuery, { id }).toPromise(),
        getTasksByObjects(client)({ id, type: "passage" }),
      ];
      const [response, tasks] = await Promise.all(promises);

      // Throw an error if one is returned
      if (response.error) {
        throw new Error(response.error);
      }

      // Push the response onto the stream (even if nothing was returned)
      if (response.data?.passageByID) {
        const passage = await convertToUIStructure(users)(programs)({
          ...response.data.passageByID,
        });
        subject.next({ ...passage, tasks });
      } else {
        subject.next(null);
      }
    } catch (err) {
      console.error(err);
    }
  },
  /**
   * Gets the current value of the stream
   */
  get: () => {
    return subject.getValue();
  },
  init: (c) => {
    client = c;
  },
  /**
   * Saves the passage data to the server, then puts the response in the stream
   */
  save:
    (users) =>
    (programs) =>
    (notes) =>
    ({ activeTasks }) =>
    (isTTSToogled) =>
    (ttsTagIdsToRegenerate) =>
    (options = {}) =>
    async (data) => {
      if (!data?.id) return Promise.resolve();
      try {
        const { tasks = [], ...rest } = data;
        const variables = convertToAPIStructure(users)(options)(rest);

        // Query the graphQL API
        const updatedTasks = activeTasks.passage
          ? await updateTasks(client)(tasks)
          : tasks;
        const response = canSave("passage")({
          actions: data._actions,
          activeTasks,
        })
          ? await client
              .mutation(updatePassageMutation, {
                id: variables.id,
                data: maskTypename(variables.data),
                notes: notes,
                ttsGenerate: isTTSToogled,
                ttsTagIdsToRegenerate: ttsTagIdsToRegenerate ?? null,
              })
              .toPromise()
          : {
              data: {
                updatePassage: { id: variables.id, ...variables.data },
              },
            };

        if (response?.error) {
          throw new Error(`Error in response: ${response.error}`);
        }

        const passage =
          canSave("passage")({ actions: data._actions, activeTasks }) &&
          response.data?.updatePassage
            ? await convertToUIStructure(users)(programs)({
                ...response.data.updatePassage,
              })
            : data;

        const optimisticPassage =
          ttsTagIdsToRegenerate?.length && passage
            ? {
                ...passage,
                ttsTagStatuses: applyOptimisticRetryStatuses(
                  passage.ttsTagStatuses,
                  ttsTagIdsToRegenerate,
                ),
              }
            : passage;

        // Push the response onto the stream (even if nothing was returned, to make it clear that there was an error)
        subject.next({ ...optimisticPassage, tasks: updatedTasks });
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
  /**
   * Write data to the stream
   */
  set: (value) => {
    subject.next(value);
  },
  /**
   * Subscribe to the stream
   *
   * @returns {Function} A function to unsubscribe from the stream
   */
  subscribe: (setState) => {
    return subject.subscribe(setState);
  },
};

/**
 * Convert passage data to what is expected by the graphQL API
 *
 * @param {Object} passage
 * @returns { id:{string}, data: {Object}}
 */
const convertToAPIStructure =
  (users) =>
  (options = {}) =>
  ({ id, ...passage }) => {
    const returnNameFromId = (array) => (val) => {
      return array.find((element) => element?.id === val)?.name;
    };
    const data = Object.keys(passage).reduce((acc, key) => {
      // console.debug(`Converting passage.${key}`, passage[key]);
      switch (key) {
        case "_actions":
        case "useScreenReader":
        case "ttsTagStatuses":
          // Remove the value entirely
          return { ...acc };
        case "accessibilityElements":
          const [, accessibilityElements] =
            getTextToSpeechTags("passage")(passage);
          passage.useTextToSpeech = accessibilityElements.length !== 0;
          return {
            ...acc,
            [key]:
              accessibilityElements.length !== 0
                ? removeLabelfromTTS(accessibilityElements)
                : null,
          };
        case "content":
          const el = document.createElement("div");
          el.innerHTML = passage.content;
          removeUnnecessaryMarkTags({
            dynamicTags: passage.dynamicTags,
            highlights: passage.highlights,
          })(el);
          unwrapElements(`o\\:p`)(el);
          return {
            ...acc,
            content: _.pipe([
              adjustTableFormattingForAPI,
              adjustMathMLSpacing,
              removeClassMsoNormalAttr,
              removeFontFamilyFromStyle,
              encodeMathMLTags,
              removeCdeIds,
              removeFontWeight400FromStyle,
              convertUTF8ToHTMLEntities, // Must be last, as DOM manipulation reconverts to UTF8
            ])(el.innerHTML),
            useScreenReader:
              passage.useScreenReader ||
              !!Array.from(el.querySelectorAll(".qti-visually-hidden")).length,
          };
        case "contentSpecialist":
        case "createdByUser":
        case "itemWriter":
        case "lastModifiedByUser":
          return {
            ...acc,
            [key]: !passage[key]
              ? null
              : {
                  id: passage[key],
                  name: returnNameFromId(users)(passage[key]),
                },
          };
        case "contentComments":
        case "referenceContentSuggestions":
        case "referenceContentComments":
        case "contentSuggestions":
          return { ...acc, [key]: JSON.stringify(passage[key] || []) };
        case "copyrightDate":
          let value = null;
          if (passage[key]) {
            // Return only the date part of the timestamp, and do it in a way that it won't mess with timezones
            const year = passage[key].getFullYear();
            const month = ("0" + (passage[key].getMonth() + 1)).substr(-2);
            const day = ("0" + passage[key].getDate()).substr(-2);
            value = `${year}-${month}-${day}`;
          }
          return { ...acc, [key]: value };
        case "dynamicTags":
          // Strip 'id' from the interactions (being added by react-hook-form)
          const dynamicTags = (Array.from(passage[key]) || []).map(
            ({ id: rhfID, ...o }) => o,
          );

          // Separate interactions into their separate type keys, adjust structures as needed
          return {
            ...acc,
            dynamicBreakParagraphTags: dynamicTags.filter(
              (o) => o.__typename === "DynamicBreakParagraphTag",
            ),
            dynamicModifyContentTags: dynamicTags.filter(
              (o) => o.__typename === "DynamicModifyContentTag",
            ),
            dynamicOrderContentTags: dynamicTags.filter(
              (o) => o.__typename === "DynamicOrderContentTag",
            ),
            dynamicRemoveLabelsTags: dynamicTags.filter(
              (o) => o.__typename === "DynamicRemoveLabelsTag",
            ),
          };
        case "graphicAssets":
          return {
            ...acc,
            [key]: Array.from(passage[key] || []).map(
              ({ fileTimestamp, name, user = null }) => {
                return {
                  fileTimestamp,
                  name,
                  user,
                };
              },
            ),
          };
        case "highlights":
          // Strip 'id' from the objects (being added by react-hook-form)
          const highlightsAndIPRs = (Array.from(passage[key]) || []).map(
            ({ id: rhfID, ...o }) => o,
          );
          return {
            ...acc,
            itemPointReferences: highlightsAndIPRs.filter(
              (o) => o.__typename === "ItemPointReference",
            ),
            highlights: highlightsAndIPRs.filter(
              (o) => o.__typename === "Highlight",
            ),
          };
        case "imageAssets":
          return {
            ...acc,
            [key]: Array.from(passage[key] || []).map(
              ({
                altText = null,
                fileTimestamp,
                identifier = null,
                longDescription = null,
                longDescriptionDetails,
                name,
                printableAsset,
                user = null,
              }) => {
                return {
                  altText,
                  fileTimestamp,
                  identifier,
                  longDescription,
                  longDescriptionDetails: longDescriptionDetails
                    ? {
                        category: longDescriptionDetails.category,
                        content: longDescriptionDetails.content,
                        requestId: longDescriptionDetails.requestId,
                      }
                    : null,
                  name,
                  printableAsset: printableAsset
                    ? {
                        name: printableAsset.name,
                        fileTimestamp: printableAsset.fileTimestamp,
                        user: printableAsset.user,
                      }
                    : null,
                  user,
                };
              },
            ),
          };
        case "items":
          // Return only the ids in the associated array
          return {
            ...acc,
            [key]: (Array.from(passage[key]) || []).map((o) =>
              _.isPlainObject(o) ? o.id : o,
            ),
          };
        case "keywords":
        case "release":
        case "restriction":
        case "standards":
          // if empty array change to null
          return {
            ...acc,
            [key]: passage[key].length !== 0 ? passage[key] : null,
          };
        case "metafileAssets":
          return {
            ...acc,
            [key]: Array.from(passage[key] || []).map(
              ({
                description,
                fileTimestamp,
                isASL,
                isPBT,
                name,
                user = null,
              }) => {
                return {
                  description,
                  fileTimestamp,
                  isASL,
                  isPBT,
                  name,
                  user,
                };
              },
            ),
          };
        case "program":
          return { ...acc, [key]: passage[key].id };
        case "referenceCitations":
          const convertedReferenceCitations = passage[key]?.map(
            ({ wordCount, ...rest }) => {
              return {
                ...rest,
                wordCount: wordCount ? wordCount : null,
              };
            },
          );
          return {
            ...acc,
            [key]: convertedReferenceCitations,
          };
        case "relationships":
          const convertedRelationships = passage[key]?.map(
            ({ program, source, type, ...rest }) => {
              const convertValue = (string) =>
                string.replace(/-|\s/g, "_").toUpperCase();
              return {
                ...rest,
                type: convertValue(type),
                source: convertValue(source),
              };
            },
          );
          return {
            ...acc,
            [key]:
              convertedRelationships && convertedRelationships.length
                ? uniqueObjects(convertedRelationships, [
                    "id",
                    "name",
                    "source",
                    "type",
                  ])
                : [],
          };
        case "averageStandardWordCount": {
          return {
            ...acc,
            [key]: calculateAverageStandardWordCount(
              passage.items,
              options.shouldIncrementItemCount,
            ),
          };
        }
        case "wordCount":
          // Return 'null' if value is empty string
          return { ...acc, [key]: passage[key] ? passage[key] : null };
        default:
          // convert empty strings back to null values
          return { ...acc, [key]: passage[key] ? passage[key] : null };
      }
    }, {});
    return {
      id,
      data: {
        ...data,
        cbtCharacterCount: getCharacterCount("cbt")(data),
        hasUnresolvedChangeTags: getHasUnresolvedChangeTags(data),
        pbtCharacterCount: getCharacterCount("pbt")(data),
      },
    };
  };

/**
 * Convert passage data to what is expected by the UI
 *
 * @param {Object} passage
 * @returns {Object}
 */
const convertToUIStructure = (users) => (programs) => async (passage) => {
  // console.debug("Calling convertToUIStructure with passage %o", passage);
  const program = programs.find((o) => passage?.program === o.id);
  const imageAssets = passage.imageAssets ? passage.imageAssets : [];
  const graphicAssets = passage.graphicAssets ? passage.graphicAssets : [];
  const metafileAssets = passage?.metafileAssets ? passage.metafileAssets : [];
  const programMetafileAssets =
    program?.metafileAssets?.filter((asset) =>
      asset?.passageAssociations?.includes(passage?.id),
    ) || [];

  const promises = [
    ...imageAssets,
    ...graphicAssets,
    ...metafileAssets,
    ...programMetafileAssets,
  ].map((asset) => {
    const objectID =
      asset?.__typename === "ProgramMetafileAsset" ? program?.id : passage?.id;
    return getAssetInfo(objectID)(asset);
  });
  return Promise.all(promises)
    .then((assets) => {
      const convertedAssets = (type) =>
        assets.filter((asset) => asset.__typename === type);

      return Object.keys(passage).reduce((acc, key) => {
        switch (key) {
          case "accessibilityElements":
          case "keywords":
          case "release":
          case "restriction":
          case "standards":
            // convert null to empty array
            return { ...acc, [key]: passage[key] ? passage[key] : [] };
          case "content":
            return {
              ...acc,
              [key]: _.pipe([
                adjustTableFormattingForUI,
                encodeMathMLTags,
                convertUTF8ToHTMLEntities, // Must be last, as DOM manipulation reconverts to UTF8
                (content) => {
                  // console.log("content in store", content);
                  return content;
                },
              ])(passage[key]),
            };
          case "contentComments":
          case "referenceContentSuggestions":
          case "referenceContentComments":
          case "contentSuggestions":
            return {
              ...acc,
              [key]: passage[key] ? JSON.parse(passage[key]) : [],
            };
          case "contentSpecialist":
          case "createdByUser":
          case "itemWriter":
          case "lastModifiedByUser":
            return {
              ...acc,
              [key]: passage[key]?.id,
            };
          case "copyrightDate":
            // Return only the date part of the timestamp
            return {
              ...acc,
              [key]: passage[key]
                ? new Date(Date.parse(`${passage[key]}T00:00:00`))
                : null,
            };
          case "firstBankedDateTime":
          case "lastBankedDateTime":
          case "createdDateTime":
          case "lastModifiedDateTime":
            return {
              ...acc,
              [key]: passage[key] ? new Date(passage[key]) : null,
            };
          case "dynamicBreakParagraphTags":
          case "dynamicModifyContentTags":
          case "dynamicOrderContentTags":
          case "dynamicRemoveLabelsTags":
            const dynamicTags = acc.dynamicTags ? acc.dynamicTags : [];
            const newDynamicTags = passage[key] || [];
            return {
              ...acc,
              dynamicTags: orderDynamicTags(passage.content)([
                ...dynamicTags,
                ...newDynamicTags,
              ]),
            };
          case "graphicAssets":
            return { ...acc, [key]: convertedAssets("GraphicAsset") };
          case "highlights":
          case "itemPointReferences":
            const highlights = acc.highlights ? acc.highlights : [];
            const newHighlights = passage[key] || [];
            return {
              ...acc,
              highlights: orderDynamicTags(passage.content)([
                ...highlights,
                ...newHighlights,
              ]),
            };
          case "imageAssets":
            return { ...acc, [key]: convertedAssets("ImageAsset") };
          case "averageStandardWordCount": {
            const recalculatedAverage = calculateAverageStandardWordCount(
              passage.items,
            );
            return {
              ...acc,
              [key]: _.isNumber(recalculatedAverage) ? recalculatedAverage : "",
            };
          }
          case "items":
            const items = passage[key]?.length ? passage[key] : [];
            return {
              ...acc,
              items: items.map(
                ({ choiceInteractions: interactions, ...item }) => ({
                  ...item,
                  interactions,
                  key: deriveKeyValueFromInteraction(interactions),
                }),
              ),
            };
          case "metafileAssets":
            return { ...acc, [key]: convertedAssets("MetafileAsset") };
          case "program":
            const { id, name, keywords, configurationID } = program;

            return {
              ...acc,
              [key]: {
                id,
                name,
                keywords,
                configurationID,
                metafileAssets: convertedAssets("ProgramMetafileAsset"),
              },
            };
          case "referenceCitations":
            const sortedRefs = _.sortBy("referenceTitle")(passage[key]);
            return { ...acc, [key]: passage[key] ? sortedRefs : [] };
          case "relationships":
            // get relationship programs and convert values for metadata table
            const convertedRelationships = passage[key]?.map(
              ({ program, type, source, ...rest }) => {
                const programName = programs.find((o) => program === o?.id);
                const convertOptions = (options) => (val) =>
                  options.find(
                    (option) =>
                      val === option.replace(/-|\s/g, "_").toUpperCase(),
                  );
                return {
                  ...rest,
                  program: programName?.name,
                  source: convertOptions(dataSourceOptions)(source),
                  type: convertOptions(relationshipTypeOptions)(type),
                };
              },
            );
            return { ...acc, [key]: convertedRelationships || [] };
          default:
            // convert null values to empty strings
            return { ...acc, [key]: passage[key] ? passage[key] : "" };
        }
      }, {});
    })
    .catch((err) => {
      console.error(err);
      throw new Error(err);
    });
};

const passageFields = `
  _actions
  accessibilityElements {
    accessibilityFeatures {
      feature
      language
      src
      text
      type
    }
    identifier
    inclusionOrders
  }
  ttsTagStatuses {
    tagId
    status
  }
  aiContent
  application
  argumentativePassageSet
  cbtCharacterCount
  cdeID
  cognitiveCode
  content
  contentArea
  contentComments
  contentLicensed
  contentSpecialist {
    id
    name
  }
  contentSuggestions
  copyrightCertificate
  copyrightDate
  createdDateTime
  createdByUser {
    id
    name
  }
  crossCurriculum
  developmentState
  dynamicBreakParagraphTags {
    items
    point
  }
  dynamicModifyContentTags {
    begin
    end
    highlight
    items
    replaceWith
    underline
  }
  dynamicOrderContentTags {
    items
    order
  }
  dynamicRemoveLabelsTags {
    items
    labels {
      begin
      end
    }
  }
  elementStatus
  ethnicity
  externalID
  firstBankedDateTime
  fleschKincaid
  foreignID
  gender
  genderRole1
  genderRole2
  genderRole3
  gradeLevel
  gradeSpanEnd
  gradeSpanStart
  graphicAssets {
    fileTimestamp
    name
    user
  }
  groupRole1
  groupRole2
  groupRole3
  hasUnresolvedChangeTags
  highlights {
    begin
    end
    items
  }
  id
  imageAssets {
    altText
    fileTimestamp
    identifier
    longDescription
    longDescriptionDetails {
      category
      content 
      requestId 
    }
    name
    user
    printableAsset {
      fileTimestamp
      name
      user
    }
  }
  importID
  income
  itemPointReferences {
    items
    point
  }
  items {
    choiceInteractions {
      foils {
        content
        correct
      }
    }
    depthOfKnowledge
    description
    developmentState
    id
    name
    pbtCharacterCount
    pretestGroup
    standards {
      humanCodingScheme
      order
    }
    type
    wordCount
    versionID
  }
  itemWriter {
    id
    name
  }
  keywords
  language
  lastBankedDateTime
  lastModifiedDateTime
  lastModifiedByUser {
    id
    name
  }
  metafileAssets {
    description
    fileTimestamp
    isASL
    isPBT
    name
    user
  }
  name
  ngss1
  ngss2
  ngss3
  pairedPassage
  passageAsAWhole
  pbtCharacterCount
  averageStandardWordCount
  pointOfView
  presentationFormat
  predictedUnitDifficulty
  primaryContentCode
  program
  readabilityIndex
  readingLevelNotes
  referenceCitations {
    author
    citation
    commissioned
    copyrightYear
    ISBN
    notes
    page
    publicationInformation
    publicDomain
    publisher
    referenceTitle
    rightsHolder
    sourceURL
    wordCount
    workTitle 
  }
  referenceContent
  referenceContentSuggestions
  referenceContentComments
  referenceDynamicValidation 
  relationships {
    id
    name
    program
    source
    type
  }
  region 
  release
  religion
  resource
  restriction
  secondaryContentCode
  secondaryType
  secondarySubType
  shortPassageSet
  standards {
    humanCodingScheme
    order
  }
  subEthnicity
  subProgram
  subRegion
  subType
  summary
  textComplexity
  topic
  type
  unavailability
  unitStrength
  universalDesign
  urbanicity
  useScreenReader
  useTextToSpeech
  versionID
  vqiPassageSet
  width
  wordCount
`;

const getPassageQuery = `
  query GetPassage($id: ID!) {
    passageByID(id: $id) { ${passageFields} }
  }`;

const updatePassageMutation = `
  mutation UpdatePassage($id: ID!, $data: PassageInput!, $notes: String, $ttsGenerate: Boolean!, $ttsTagIdsToRegenerate: [String]) {
    updatePassage(id: $id, data: $data, notes: $notes, ttsGenerate: $ttsGenerate, ttsTagIdsToRegenerate: $ttsTagIdsToRegenerate) { ${passageFields} }
  }`;

export {
  calculateAverageStandardWordCount,
  convertToAPIStructure,
  convertToUIStructure,
  getPassageQuery,
  passageFields,
  store,
  updatePassageMutation,
};
